import { observer } from 'mobx-react-lite'
import { type JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { type JobConsultationViewModel } from '@/features/jobs/details/JobConsultationViewModel'
import { ConsultationPendingBookingModalContainer } from '@/features/jobs/details/components/ConsultationPendingBookingModal/ConsultationPendingBookingModalContainer'
import { Card } from '@/components/Card'
import { consultationCallProgress } from '@/features/jobs/consultations/consultation-call-progress'
import { RoomCardContainer } from '@/features/jobs/details/components/RoomCard/RoomCardContainer'
import {
  CompleteConsultationJobButton,
  ConsultationProgressBar,
  DownloadCalendarButton,
  PendingSchedulingContent,
  RoomCardDescription,
  RoomCardTitle,
} from '@/features/jobs/details/components/ConsultationCallCard'

export interface ConsultationCallCardContainerProps {
  vm: JobDetailsViewModel
}

export const ConsultationCallCardContainer = observer(
  function ConsultationCallCardContainer({
    vm,
  }: ConsultationCallCardContainerProps) {
    const consultation = vm.job.consultation
    if (!consultation) {
      return null
    }

    if (vm.consultationVm.consultationIsPendingScheduling) {
      return (
        <Card>
          <PendingSchedulingContentContainer vm={vm.consultationVm} />
        </Card>
      )
    }

    const callStep = consultationCallProgress[consultation.callStep]

    return (
      <RoomCardContainer
        vm={vm.consultationVm.roomVm}
        titleContent={
          <RoomCardTitle
            skipScheduling={consultation.skipScheduling}
            callStep={consultation.callStep}
          />
        }
        descriptionContent={
          <RoomCardDescription
            startTime={consultation.bookedTime?.startTime}
            skipScheduling={consultation.skipScheduling}
            callStep={consultation.callStep}
          />
        }
        progressBar={
          !consultation.skipScheduling && (
            <ConsultationProgressBar
              completed={callStep.completed}
              pending={callStep.pending}
            />
          )
        }
        buttonGroup={
          <>
            {consultation.canClose && (
              <CompleteConsultationJobButton
                onPress={vm.requestCloseAsCollaborator}
              />
            )}
            <DownloadCalendarButtonContainer vm={vm.consultationVm} />
          </>
        }
      />
    )
  },
)

export interface PendingSchedulingContentContainerProps {
  vm: JobConsultationViewModel
}

export const PendingSchedulingContentContainer = observer(
  function PendingSchedulingContentContainer({
    vm,
  }: PendingSchedulingContentContainerProps) {
    return (
      <PendingSchedulingContent
        isPendingBooking={vm.canPickTime}
        bookingModal={
          <ConsultationPendingBookingModalContainer
            vm={vm.consultationPendingBookingModalVm}
          />
        }
        previouslyBooked={vm.previouslyBooked}
        onSelectAppointment={vm.promptForTimeSlotSelection}
        isSelectAppointmentPending={vm.promptForTimeSlotSelection.pending}
        pendingSchedulingText={vm.consultationPendingSchedulingText}
      />
    )
  },
)

export interface DownloadCalendarButtonContainerProps {
  vm: JobConsultationViewModel
}

export const DownloadCalendarButtonContainer = observer(
  function DownloadCalendarButtonContainer({
    vm,
  }: DownloadCalendarButtonContainerProps) {
    return (
      vm.isConsultationBookedAndActive && (
        <DownloadCalendarButton
          isPending={vm.downloadConsultationCalendar.pending}
          onPress={vm.downloadConsultationCalendar}
        />
      )
    )
  },
)
